<template>
  <div class="admin-dashboard">
    <AdminHeader />
    <main>
      <b-container>
        <div class="mt-5">
          <b-card>
            <b-card-body class="p-0">
              <b-card-title>Manage Admin Users</b-card-title>

              <b-alert :show="!!errorMessage" variant="danger" dismissible @dismissed="errorMessage = ''">{{
                errorMessage
              }}</b-alert>
              <b-alert :show="!!successMessage" variant="success" dismissible @dismissed="successMessage = ''">{{
                successMessage
              }}</b-alert>

              <!-- Create admin -->
              <validation-observer ref="observer" v-slot="{ handleSubmit }">
                <b-form class="mb-4" @submit.prevent="handleSubmit(onCreate)">
                  <b-row>
                    <b-col md="5">
                      <b-form-group label="Email:" label-for="input-admin-email">
                        <validation-provider
                          name="Email"
                          rules="required|email"
                          v-slot="validationContext"
                        >
                          <b-form-input
                            id="input-admin-email"
                            v-model="newAdmin.email"
                            :state="getValidationState(validationContext)"
                            placeholder="Enter admin email"
                          ></b-form-input>
                          <b-form-invalid-feedback>{{
                            validationContext.errors[0]
                          }}</b-form-invalid-feedback>
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                    <b-col md="5">
                      <b-form-group label="Name (optional):" label-for="input-admin-name">
                        <b-form-input
                          id="input-admin-name"
                          v-model="newAdmin.name"
                          placeholder="Enter name for the welcome email"
                        ></b-form-input>
                      </b-form-group>
                    </b-col>
                    <b-col md="2" class="d-flex align-items-end">
                      <b-form-group class="w-100">
                        <b-button type="submit" variant="primary" :disabled="creating" block>
                          <font-awesome-icon icon="user-plus" /> Add Admin
                        </b-button>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-form>
              </validation-observer>

              <!-- Admin list -->
              <b-table
                striped
                responsive
                ref="table"
                stacked="lg"
                :fields="fields"
                :items="items"
                :busy="loading"
              >
                <template #table-busy>
                  <div class="text-center my-3">
                    <b-spinner class="align-middle"></b-spinner>
                  </div>
                </template>
                <template #cell(actions)="data">
                  <b-button
                    variant="outline-danger"
                    size="sm"
                    :disabled="!canDelete(data.item)"
                    :title="deleteTooltip(data.item)"
                    @click="onDelete(data.item)"
                  >
                    Delete
                  </b-button>
                </template>
              </b-table>
            </b-card-body>
          </b-card>
        </div>
      </b-container>
    </main>
  </div>
</template>

<script>
import AdminHeader from "@/components/admin/Header.vue";
import AdminUsersService from "@/services/AdminUsersService";

export default {
  name: "ManageAdmins",
  components: {
    AdminHeader,
  },
  data() {
    return {
      items: [],
      fields: [
        { label: "Email", key: "email" },
        { label: "Username", key: "username" },
        { label: "", key: "actions" },
      ],
      newAdmin: {
        email: "",
        name: "",
      },
      loading: false,
      creating: false,
      errorMessage: "",
      successMessage: "",
    };
  },
  computed: {
    currentUserId() {
      return this.$store.state.auth.user ? this.$store.state.auth.user.id : null;
    },
    isLastAdmin() {
      return this.items.length <= 1;
    },
  },
  async mounted() {
    await this.loadAdmins();
  },
  methods: {
    getValidationState({ dirty, validated, valid = null }) {
      return dirty || validated ? valid : null;
    },
    isSelf(item) {
      return item.id === this.currentUserId;
    },
    canDelete(item) {
      // Guard rails mirrored from the server: never the last admin, never yourself.
      return !this.isLastAdmin && !this.isSelf(item);
    },
    deleteTooltip(item) {
      if (this.isSelf(item)) return "You cannot delete your own account.";
      if (this.isLastAdmin) return "At least one admin must remain.";
      return "Delete this admin user";
    },
    async loadAdmins() {
      this.loading = true;
      try {
        const response = await AdminUsersService.GetAdmins();
        this.items = response.data || [];
      } catch (e) {
        this.errorMessage = this.extractError(e, "Unable to load admin users.");
      } finally {
        this.loading = false;
      }
    },
    async onCreate() {
      this.creating = true;
      this.errorMessage = "";
      this.successMessage = "";
      try {
        const response = await AdminUsersService.CreateAdmin({
          email: this.newAdmin.email.trim(),
          name: this.newAdmin.name ? this.newAdmin.name.trim() : "",
        });
        this.successMessage =
          (response && response.message) || "Admin user created successfully.";
        this.newAdmin.email = "";
        this.newAdmin.name = "";
        this.$refs.observer.reset();
        await this.loadAdmins();
      } catch (e) {
        this.errorMessage = this.extractError(e, "Unable to create admin user.");
      } finally {
        this.creating = false;
      }
    },
    async onDelete(item) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Delete admin "${item.email}"? This cannot be undone.`,
        {
          title: "Please Confirm",
          okVariant: "danger",
          okTitle: "Delete",
          cancelTitle: "Cancel",
          centered: true,
        }
      );

      if (!confirmed) return;

      this.errorMessage = "";
      this.successMessage = "";
      try {
        await AdminUsersService.DeleteAdmin(item.id);
        this.successMessage = `Admin "${item.email}" deleted.`;
        await this.loadAdmins();
      } catch (e) {
        this.errorMessage = this.extractError(e, "Unable to delete admin user.");
      }
    },
    extractError(e, fallback) {
      if (e && e.response && e.response.data && e.response.data.message) {
        return e.response.data.message;
      }
      return fallback;
    },
  },
};
</script>
